import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import DataTable from '../data_table/DataTable';
import Button from '../ui/Button';
import LimitTrack, { NamesChangedTrack } from './LimitTrack';
import RuleLimitInput from './RuleLimitInput';
import RuleStatusChip from './RuleStatusChip';
import { buildManualConstructionRows } from './ManualConstructionRows';
import { MANUAL_OBJECTIVES, MAX_SHARPE_OBJECTIVE } from '../../utils/optimizerObjectives';
import {
    RULE_TABLE_CLASS_NAME,
    RULE_TABLE_HEADER_HEIGHT,
    RuleLabel,
    RuleValue,
    renderRuleFullWidthRow,
    ruleColumns,
    ruleModeControl,
    ruleNoteClassName,
    ruleSwitchedOff,
} from './OptimizerRuleTableParts';
import {
    PORTFOLIO_RULES,
    TRADING_RULES,
    SECURITY_CONTROL_RULES,
    CARDINALITY_RULE,
    describeRuleResult,
    formatRuleValue,
    portfolioRuleId,
    ruleEditState,
} from './optimizerRuleRegistry';

function valueTooltip(rule, evaluation, side) {
    if (!evaluation) return null;
    const detail = evaluation.detail ?? {};
    if (rule.key === 'position_gmv_pct_of_aum') {
        const ticker = detail[`${side}_ticker`];
        return ticker ? `Largest position: ${ticker}` : null;
    }
    if (rule.key === 'ticker_gmv_pct_delta') {
        if (side !== 'optimized') return null;
        const opened = detail.unheld_names_opened?.length ?? 0;
        return [
            detail.optimized_ticker ? `Largest trade: ${detail.optimized_ticker}` : null,
            opened
                ? `${opened} name${opened === 1 ? '' : 's'} opened without an existing position`
                : null,
        ].filter(Boolean).join(' · ') || null;
    }
    if (rule.key === 'turnover_pct_gmv') {
        return side === 'optimized' && detail.traded_usd != null
            ? `${formatRuleValue(detail.traded_usd, 'usd')} traded`
            : null;
    }
    if (rule.key === 'position_days_adv' || rule.key === 'trade_pct_adv') {
        const ticker = detail[`${side}_ticker`];
        return ticker ? `Least liquid versus its limit: ${ticker}` : null;
    }
    const usd = detail[`${side}_usd`];
    return usd == null ? null : formatRuleValue(usd, 'usd');
}

// Per-name controls are set in the securities table. The row counts the
// checked names carrying the control and reports whether the run respected it.
function securityControlRow(rule, count, evaluation, stale) {
    const result = describeRuleResult(evaluation, rule);
    return {
        id: rule.ruleId,
        cells: {
            rule: (
                <RuleLabel
                    label={rule.label}
                    unitLabel="set per name in the securities table"
                    description={rule.description}
                />
            ),
            mode: <span className={ruleNoteClassName}>Per name</span>,
            limit: <span className="tabular-nums">{`${count} name${count === 1 ? '' : 's'}`}</span>,
            baseline: <RuleValue value={null} unit="count" />,
            optimized: <RuleValue value={null} unit="count" />,
            track: <span className={ruleNoteClassName}>{result?.headroom ?? '—'}</span>,
            status: <RuleStatusChip evaluation={evaluation} rule={rule} stale={stale} />,
        },
    };
}

const hintClassName = 'mt-0.5 block text-[0.62rem] leading-[1.2] text-content-secondary';

// Where the GMV budget comes from: the checked names' baseline GMV, or a value
// the user typed (with a way back to the checked names).
function GmvBudgetHint({ gmvBudget, disabled }) {
    const checked = gmvBudget.checkedGmv;
    if (gmvBudget.auto) {
        return (
            <span className={hintClassName}>
                {checked == null
                    ? 'Checked names hold no positions; enter a budget'
                    : 'Follows the checked names'}
            </span>
        );
    }
    return (
        <span className={hintClassName}>
            {`Checked names: ${checked == null ? '—' : formatRuleValue(checked, 'usd')}`}
            {checked != null && (
                <Button size="sm" disabled={disabled} onClick={gmvBudget.followChecked} className="ml-1">
                    Use
                </Button>
            )}
        </span>
    );
}

GmvBudgetHint.propTypes = {
    gmvBudget: PropTypes.shape({
        auto: PropTypes.bool.isRequired,
        checkedGmv: PropTypes.number,
        followChecked: PropTypes.func.isRequired,
    }).isRequired,
    disabled: PropTypes.bool,
};

export default function PortfolioRulesTable({
    constraints,
    constraintToggles,
    gmvBudget = null,
    evaluationIndex,
    baselineLabel,
    actions,
    resultStale = false,
    disabled = false,
    cardinalityAvailable = false,
    securityControlCounts = null,
    objective = 'AlphaScore',
    onViewChangedNames = null,
    searchRanges = null,
    rangeEditor = null,
    modeEditor = null,
}) {
    const values = constraints?.static_constraints ?? {};
    const toggles = constraintToggles?.static_constraints ?? {};
    const hasModeEditor = Boolean(modeEditor);
    const hasRangeEditor = Boolean(rangeEditor);
    const columns = useMemo(() => ruleColumns([
        { id: 'rule', header: 'Rule', width: 22 },
        { id: 'mode', header: hasModeEditor ? 'Mode' : 'On', width: 7, align: 'center' },
        {
            id: 'limit',
            header: hasRangeEditor ? 'Limit / range' : 'Limit',
            width: hasRangeEditor ? 25 : 16,
            align: 'right',
        },
        { id: 'baseline', header: baselineLabel, width: 12, align: 'right', wrap: false },
        { id: 'optimized', header: 'Optimized', width: 12, align: 'right', wrap: false, optimized: true },
        { id: 'track', header: 'Result vs limit', width: 19, align: 'right' },
        { id: 'status', header: 'Status', width: 12 },
    ], hasRangeEditor ? 960 : 760), [baselineLabel, hasModeEditor, hasRangeEditor]);

    const ruleRow = (rule) => {
        const enabled = rule.alwaysApplied || toggles[rule.key] === true;
        const cardinality = rule.key === 'max_names_changed';
        const maxAlphaOnly = cardinality || TRADING_RULES.includes(rule);
        const evaluation = evaluationIndex.byRuleId.get(portfolioRuleId(rule));
        const { changed, draftLimit } = ruleEditState(evaluation, {
            enabled,
            value: values[rule.key] ?? null,
            explore: Boolean(searchRanges?.[portfolioRuleId(rule)]),
        });
        let mode = <span className={ruleNoteClassName}>Always</span>;
        if (!rule.alwaysApplied) {
            mode = ruleModeControl(modeEditor, {
                parameter: portfolioRuleId(rule),
                enabled,
                value: values[rule.key],
                label: rule.label,
                disabled,
                onEnabledChange: (value) => actions.setPortfolioRuleEnabled(rule.key, value),
            });
        }
        return {
            id: rule.key,
            cells: {
                rule: (
                    <>
                        <RuleLabel
                            label={rule.label}
                            unitLabel={cardinality
                                ? `${rule.unitLabel} · vs. ${baselineLabel.replace('Baseline (', '').replace(')', '')}`
                                : rule.unitLabel}
                            description={rule.description}
                        />
                        {maxAlphaOnly && !(objective in MANUAL_OBJECTIVES) && objective !== 'PortfolioDesign'
                            && objective !== MAX_SHARPE_OBJECTIVE && (
                            <span className="mt-0.5 block text-[0.64rem] text-ui-warning">
                                Available only with Max Alpha or Max Sharpe
                            </span>
                        )}
                    </>
                ),
                mode,
                limit: (
                    <>
                        {rangeEditor && !rule.alwaysApplied ? rangeEditor({
                            parameter: portfolioRuleId(rule), value: values[rule.key],
                            unit: rule.unit, form: rule.form, label: rule.label, disabled: disabled || !enabled,
                            onChange: (value) => actions.setPortfolioRuleValue(rule.key, value),
                        }) : (
                            <RuleLimitInput
                                value={values[rule.key] ?? null}
                                onChange={(value) => actions.setPortfolioRuleValue(rule.key, value)}
                                unit={rule.unit}
                                form={rule.form}
                                disabled={!enabled || disabled}
                                ariaLabel={`${rule.label} limit`}
                                width={rule.unit === 'usd' ? 140 : 108}
                            />
                        )}
                        {rule.key === 'gmv' && gmvBudget && (
                            <GmvBudgetHint gmvBudget={gmvBudget} disabled={disabled} />
                        )}
                    </>
                ),
                baseline: (
                    <RuleValue
                        value={evaluation?.baseline ?? null}
                        unit={rule.unit}
                        muted={ruleSwitchedOff(evaluation)}
                    />
                ),
                optimized: (
                    <RuleValue
                        value={evaluation?.optimized ?? null}
                        unit={rule.unit}
                        muted={ruleSwitchedOff(evaluation)}
                        tooltip={valueTooltip(rule, evaluation, 'optimized')}
                    />
                ),
                track: cardinality && evaluation
                    ? <NamesChangedTrack changed={evaluation.optimized} limit={evaluation.limit} />
                    : (
                        <LimitTrack
                            evaluation={evaluation}
                            rule={rule}
                            label={rule.label}
                            baselineLabel={baselineLabel}
                            draftLimit={draftLimit}
                        />
                    ),
                status: (
                    <>
                        <RuleStatusChip
                            evaluation={evaluation}
                            rule={rule}
                            changed={changed}
                            stale={resultStale}
                        />
                        {cardinality && evaluation && (
                            <span className="mt-0.5 block">
                                <Button size="sm" onClick={onViewChangedNames}>View names</Button>
                            </span>
                        )}
                    </>
                ),
            },
        };
    };

    const rows = [
        ...buildManualConstructionRows({
            constraints, objective, actions, evaluationIndex, baselineLabel, disabled, resultStale, rangeEditor, modeEditor, searchRanges,
        }),
        ...[
            ...PORTFOLIO_RULES,
            ...TRADING_RULES,
            ...(cardinalityAvailable ? [CARDINALITY_RULE] : []),
        ].map(ruleRow),
        ...SECURITY_CONTROL_RULES.flatMap((rule) => {
            const count = securityControlCounts?.[rule.control] ?? 0;
            const evaluation = evaluationIndex.byRuleId.get(rule.ruleId);
            if (!count && !evaluation) return [];
            return [securityControlRow(rule, count, evaluation, resultStale)];
        }),
    ];

    return (
        <DataTable
            aria-label="Portfolio constraints"
            data={rows}
            columns={columns}
            renderFullWidthRow={renderRuleFullWidthRow}
            headerHeight={RULE_TABLE_HEADER_HEIGHT}
            wrapHeaders
            className={RULE_TABLE_CLASS_NAME}
        />
    );
}

PortfolioRulesTable.propTypes = {
    constraints: PropTypes.object.isRequired,
    constraintToggles: PropTypes.object.isRequired,
    gmvBudget: PropTypes.shape({
        auto: PropTypes.bool,
        checkedGmv: PropTypes.number,
        followChecked: PropTypes.func,
    }),
    evaluationIndex: PropTypes.shape({ byRuleId: PropTypes.instanceOf(Map) }).isRequired,
    baselineLabel: PropTypes.string.isRequired,
    actions: PropTypes.object.isRequired,
    resultStale: PropTypes.bool,
    disabled: PropTypes.bool,
    cardinalityAvailable: PropTypes.bool,
    securityControlCounts: PropTypes.objectOf(PropTypes.number),
    objective: PropTypes.string,
    onViewChangedNames: PropTypes.func,
};
